import React, { useRef, useEffect, useState } from "react";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts/highstock";
import highchartsMore from "highcharts/highcharts-more.js";
import xRange from "highcharts/modules/xrange.js";
import { Box } from "@material-ui/core";

highchartsMore(Highcharts);
xRange(Highcharts);

Highcharts.setOptions({
    lang: {
        resetZoom: "Quitar zoom",
        months: [
            "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
            "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
        ],
        shortMonths: [
            "Ene", "Feb", "Mar", "Abr", "May", "Jun",
            "Jul", "Ag", "Sep", "Oct", "Nov", "Dic"
        ],
        weekdays: [
            "Domingo", "Lunes", "Martes", "Miercoles", "Jueves", "Viernes", "Sabado"
        ],
        shortWeekdays: ["D", "L", "M", "Mi", "J", "V", "S"],
    },
});

export function ObjectiveZoomGraph({ categories, series, openModal, onSelectEvent }) {
    const [containerHeight, setContainerHeight] = useState(50);
    const containerRef = useRef(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleLabelClick = (e) => {
            const labelEl = e.target.closest(".graph-label-container") || e.target.closest(".highcharts-yaxis-label");
            if (labelEl) {
                const categoryName = labelEl.getAttribute("data-category") || labelEl.innerText.trim();
                if (categoryName && openModal) {
                    e.stopPropagation();
                    openModal(categoryName);
                }
            }
        };

        container.addEventListener("click", handleLabelClick);
        return () => {
            container.removeEventListener("click", handleLabelClick);
        };
    }, [openModal]);

    function generateBaseOptions(categories, series) {
        // 1. Extraemos solo los nombres para que Highcharts renderice texto plano en el eje Y
        const categoryNames = categories.map((cat) =>
            typeof cat === "string" ? cat : cat?.nombre || ""
        );

        const updatedSeries = series.map((s) => ({
            ...s,
            pointWidth: 15,
        }));

        return {
            chart: {
                type: "xrange",
                zoomType: "x",
                scrollablePlotArea: {
                    minWidth: 1000,
                },
            },
            title: { text: null },
            xAxis: {
                type: "datetime",
                gridLineWidth: 0,
                minRange: 60 * 1000,
                dateTimeLabelFormats: {
                    day: "%e %b %Y",
                    hour: "%H:%M",
                    minute: "%H:%M",
                },
                tickPixelInterval: 200,
                minTickInterval: 200,
                units: [
                    ["minute", [5, 15, 30]],
                    ["hour", [1, 2]],
                    ["day", [1]],
                ],
                opposite: true,
            },
            yAxis: {
                title: { text: "" },
                labels: {
                    useHTML: true,
                    style: {
                        whiteSpace: "normal",
                        cursor: "pointer",
                    },
                    formatter: function () {
                        return (
                            `<div class="graph-label-container" data-category="${this.value}" style="word-wrap: break-word; word-break: break-all; cursor: pointer;">` +
                            this.value +
                            "</div>"
                        );
                    },
                },
                categories: categoryNames, // <--- Pasamos solo los nombres
            },
            series: updatedSeries,
            plotOptions: {
                series: {
                    borderRadius: 0,
                    borderColor: "transparent",
                    pointWidth: 10,
                    turboThreshold: 0,
                    grouping: false,
                    cursor: "pointer",
                    point: {
                        events: {
                            click: function (e) {
                                const point = e.point;

                                // 2. point.y es el índice entero de la categoría; recuperamos el objeto original
                                const rawCategory = categories[point.y];

                                // Formateamos el objeto asegurando objetivo_id y nombre
                                const targetCategory =
                                    typeof rawCategory === "string"
                                        ? { objetivo_id: null, nombre: rawCategory }
                                        : rawCategory || { objetivo_id: null, nombre: "" };
                                const eventData = {
                                    id: Date.now() + Math.random(),
                                    objetivos: targetCategory.nombre ? [targetCategory] : [],
                                    fechaInicio: point.x,
                                    fechaFin: point.x2,
                                    estado: point.series?.name || "",
                                };
                                if (onSelectEvent) {
                                    onSelectEvent(eventData);
                                }
                            },
                        },
                    },
                },
            },
            legend: { enabled: false },
            credits: { enabled: false },
            tooltip: {
                backgroundColor: "rgba(255,255,255,0)",
                shadow: false,
                useHTML: true,
                className: "general-status",
                xDateFormat: "%A, %b %e, %Y %H:%M:%S",
                padding: 1,
            },
        };
    }

    const chartOptions = generateBaseOptions(categories, series);
    const categoriesQ = categories.length;

    return (
        <Box ref={containerRef} sx={{ height: containerHeight }}>
            <HighchartsReact
                key={categories.length}
                highcharts={Highcharts}
                options={chartOptions}
                callback={(chart) => {
                    const height = 400 - chart.plotBox.height + categoriesQ * 20;
                    chart.setSize(null, height);
                    chart.hasUserSize = null;
                    setContainerHeight(height);
                }}
            />
        </Box>
    );
}